import LocalStorage from '@/utils/localstorage'

export interface Permission {
  path: string
  create: boolean
  read: boolean
  update: boolean
  delete: boolean
}

export interface RoleAccessData {
  permissions?: Array<Permission>
}

type PermissionType = 'create' | 'read' | 'update' | 'delete'

class RoleAccessManager {
  private STORAGE_KEY = 'role_access'
  private USER_STORAGE_KEY = 'user'
  private LAST_UPDATE_KEY = 'role_access_last_update'
  private UPDATE_INTERVAL = 60 * 60 * 1000 * 2 // 2 hours in milliseconds
  private updateTimer: any = null
  private updateCallback: (() => void) | null = null

  /**
   * Get active_role_id dari user data di localStorage
   */
  private getActiveRoleId(): number | null {
    try {
      const userData = LocalStorage.getOne(this.USER_STORAGE_KEY)
      if (userData) {
        const user = JSON.parse(userData)
        return user?.active_role_id || null
      }
    } catch (error) {
      console.error('Error retrieving user data:', error)
    }
    return null
  }

  /**
   * Check apakah user adalah super admin (role_id = 1)
   */
  private isSuperAdmin(): boolean {
    const activeRoleId = this.getActiveRoleId()
    return activeRoleId == 1
  }

  /**
   * Store role access data ke localStorage
   * Data bisa berupa array langsung atau object dengan nested permissions
   */
  setRoleAccess(data: Permission[] | RoleAccessData): void {
    try {
      let rolesData: Permission[] = []
      
      // Handle berbagai format input
      if (Array.isArray(data)) {
        rolesData = data
      } else if (data && Array.isArray(data.permissions)) {
        rolesData = data.permissions
      }
      
      // Store sebagai array permissions
      LocalStorage.set(this.STORAGE_KEY, JSON.stringify(rolesData))
      // Update last update timestamp
      LocalStorage.set(this.LAST_UPDATE_KEY, JSON.stringify(new Date().getTime()))
    } catch (error) {
      console.error('Error storing role access:', error)
    }
  }

  /**
   * Get semua role access data sebagai array
   */
  getRoleAccess(): Permission[] {
    try {
      const data = LocalStorage.getOne(this.STORAGE_KEY)
      if (data) {
        const parsed = JSON.parse(data)
        // Ensure it's always an array
        return Array.isArray(parsed) ? parsed : parsed.permissions || []
      }
    } catch (error) {
      console.error('Error retrieving role access:', error)
    }
    return []
  }

  /**
   * Check if role_access data needs to be refreshed
   */
  private shouldUpdateRoleAccess(): boolean {
    try {
      const lastUpdateStr = LocalStorage.getOne(this.LAST_UPDATE_KEY)
      if (!lastUpdateStr) {
        return true
      }
      
      const lastUpdate = JSON.parse(lastUpdateStr)
      const now = new Date().getTime()
      return (now - lastUpdate) > this.UPDATE_INTERVAL
    } catch (error) {
      console.error('Error checking update time:', error)
      return true
    }
  }

  /**
   * Start auto-updater untuk role_access
   * Will fetch immediately on first load, then every 2 hours
   * If data changed, will prompt user to refresh page
   */
  startAutoUpdate(fetchCallback: () => Promise<Permission[] | RoleAccessData>, onDataChanged?: () => void): void {
    // Clear existing timer jika ada
    if (this.updateTimer) {
      clearInterval(this.updateTimer)
    }

    // Set callback untuk update
    this.updateCallback = async () => {
      try {
        const newData = await fetchCallback()
        
        // Normalize newData ke array
        let newDataArray: Permission[] = []
        if (Array.isArray(newData)) {
          newDataArray = newData
        } else if (newData && Array.isArray(newData.permissions)) {
          newDataArray = newData.permissions
        }
        
        // Get current data
        const currentData = this.getRoleAccess()
        
        // Check if data has changed
        if (this.hasDataChanged(currentData, newDataArray)) {
          // Update data di localStorage
          this.setRoleAccess(newDataArray)
          
          // Call callback untuk notify parent (akan trigger refresh dialog)
          if (onDataChanged) {
            onDataChanged()
          }
        } else {
          // Data unchanged, no need to refresh
        }
      } catch (error) {
        console.error('Error updating role access:', error)
      }
    }

    // Fetch immediately on first load
    if (this.updateCallback) {
      this.updateCallback()
    }

    // Then update setiap 5 menit
    this.updateTimer = setInterval(async () => {
      if (this.updateCallback) {
        await this.updateCallback()
      }
    }, this.UPDATE_INTERVAL)
  }

  /**
   * Compare old and new data to detect changes
   */
  private hasDataChanged(oldData: Permission[], newData: Permission[]): boolean {
    if (oldData.length !== newData.length) {
      return true
    }

    // Deep compare each permission
    for (let i = 0; i < oldData.length; i++) {
      const old = oldData[i]
      const newP = newData[i]
      
      if (!newP || 
          old.path !== newP.path ||
          old.create !== newP.create ||
          old.read !== newP.read ||
          old.update !== newP.update ||
          old.delete !== newP.delete) {
        return true
      }
    }

    return false
  }

  /**
   * Stop auto-updater
   */
  stopAutoUpdate(): void {
    if (this.updateTimer) {
      clearInterval(this.updateTimer)
      this.updateTimer = null
    }
    this.updateCallback = null
  }

  /**
   * Clear role access data
   */
  clearRoleAccess(): void {
    LocalStorage.remove(this.STORAGE_KEY)
    LocalStorage.remove(this.LAST_UPDATE_KEY)
    this.stopAutoUpdate()
  }

  /**
   * Check apakah user memiliki akses terhadap path dengan permission type tertentu
   * @param path - path/route yang ingin diakses (misal: '/bill', '/payment')
   * @param permissionType - jenis permission: 'create' | 'read' | 'update' | 'delete'
   * @returns boolean - true jika ada akses, false jika tidak atau data tidak ada
   */
  hasAccess(path: string, permissionType: PermissionType = 'read'): boolean {
    // Super admin (role_id = 1) memiliki akses ke semua CRUD
    if (this.isSuperAdmin()) {
      return true
    }

    const permissions = this.getRoleAccess()
    
    if (!permissions || permissions.length === 0) {
      console.warn('No role access data available')
      return false
    }

    // Find permission untuk path yang diminta
    const permission = permissions.find(p => p.path === path)
    
    if (!permission) {
      console.warn(`No permission found for path: ${path}`)
      return false
    }

    return permission[permissionType] === true
  }

  /**
   * Check apakah user memiliki permission untuk read/view path
   */
  canRead(path: string): boolean {
    return this.hasAccess(path, 'read')
  }

  /**
   * Check apakah user memiliki permission untuk create
   */
  canCreate(path: string): boolean {
    return this.hasAccess(path, 'create')
  }

  /**
   * Check apakah user memiliki permission untuk update
   */
  canUpdate(path: string): boolean {
    return this.hasAccess(path, 'update')
  }

  /**
   * Check apakah user memiliki permission untuk delete
   */
  canDelete(path: string): boolean {
    return this.hasAccess(path, 'delete')
  }

  /**
   * Get semua permission untuk path tertentu
   */
  getPathPermissions(path: string): Permission {
    // Super admin memiliki semua permission untuk semua path
    if (this.isSuperAdmin()) {
      return {
        path,
        create: true,
        read: true,
        update: true,
        delete: true
      }
    }

    const permissions = this.getRoleAccess()
    
    if (!permissions || permissions.length === 0) {
      return {
        path,
        create: false,
        read: false,
        update: false,
        delete: false
      }
    }

    const permission = permissions.find(p => p.path === path)
    return permission || {
      path,
      create: false,
      read: false,
      update: false,
      delete: false
    }
  }

  /**
   * Get semua paths yang user boleh akses dengan permission tertentu
   */
  getAccessiblePaths(permissionType: PermissionType = 'read'): string[] {
    // Super admin bisa akses semua paths
    if (this.isSuperAdmin()) {
      const permissions = this.getRoleAccess()
      if (permissions && permissions.length > 0) {
        return permissions.map(p => p.path)
      }
      return []
    }

    const permissions = this.getRoleAccess()
    
    if (!permissions || permissions.length === 0) {
      return []
    }

    return permissions
      .filter(p => p[permissionType] === true)
      .map(p => p.path)
  }
}

export default new RoleAccessManager()
